import { useMemo, useState } from "react";

import {
  CheckRoundIcon,
  Icon,
  PencilIcon,
  TickIcon,
} from "@vericus/cadmus-icons";
import { Button, colors, Switch, Text } from "@vericus/cadmus-ui";

import { useJitsu } from "@jitsu/react";
import { useParams } from "react-router-dom";

import assessmentImg from "@/assets/illustrations/edit-release.png";
import checkboxImg from "@/assets/illustrations/student-with-checkbox-list.png";
import { ApiError } from "@/components/errors";
import { LoadingPage } from "@/components/loading-page";
import { JitsuEvent } from "@/data/events/JitsuEvent";
import {
  PeerOverviewQuery,
  useCompletePeerOutcomesMutation,
  usePeerOverviewQuery,
} from "@/generated/graphql";
import { useRootNavigate } from "@/routes";

import { PeerOverviewTable } from "../components/peer-overview-table";
import {
  getPendingPeerEvaluations,
  PeerOutcomeTable,
  transformPeerOutcomesTable,
} from "../utils/utils";
import * as styles from "./home-page.css";

export function PeerOverviewPageWithData() {
  const { assessmentId = "" } = useParams();
  const { track } = useJitsu();
  const { data, error, loading } = usePeerOverviewQuery({
    variables: {
      assessmentId,
    },
    onCompleted: () => {
      track(JitsuEvent.PEER_EVALUATION_OPENED);
    },
  });

  const [completePeerOutcomes, { loading: loadingComplete }] =
    useCompletePeerOutcomesMutation({
      variables: {
        assessmentId,
      },
    });

  if (loading || loadingComplete) {
    return (
      <div className={styles.loadingContainer}>
        <LoadingPage />
      </div>
    );
  }
  if (error || !data) return <ApiError />;

  return <PeerOverviewPage data={data} onComplete={completePeerOutcomes} />;
}

interface Props {
  data: PeerOverviewQuery;
  onComplete: () => void;
}

/**
 * Page in Aphrodite HomePage with a overview table of the PeerOutcomes filled
 * by the current student, including self-evaluation.
 *
 * After every PeerOutcome is filled, the student can complete their Criteria
 * selections (aka CriterionOutcomes).
 */
function PeerOverviewPage(props: Props) {
  const { data, onComplete } = props;

  const groupPeerOutcomes = data.groupPeerOutcomes;
  const currentMarkerId = data.me.id;
  const peerRubric = data.peerRubric;
  const group = data.studentWorkGroup?.group;
  const selfEvaluationEnabled =
    data.assessment.settings?.selfEvaluationEnabled ?? false;

  const rootNavigate = useRootNavigate();

  // Toggle state for filtering pending evaluations
  const [filterPending, setFilterPending] = useState(false);

  // Check if all the "enabled" PeerOutcomes have been completed
  // CriterionOutcomes by the current student marker.
  const isPeerCompleted = useMemo(() => {
    if (groupPeerOutcomes.length === 0) {
      return false;
    }
    return groupPeerOutcomes.every((peerOutcome) => {
      if (peerOutcome.targetUserId === currentMarkerId) {
        if (selfEvaluationEnabled) {
          return peerOutcome.givenCriterionOutcomes.every(
            (out) => !!out.publishedAt
          );
        }
        return true;
      } else {
        return peerOutcome.givenCriterionOutcomes.every(
          (out) => !!out.publishedAt
        );
      }
    });
  }, [groupPeerOutcomes, selfEvaluationEnabled, currentMarkerId]);

  const { peerOutcomeTable, criteriaMap } = useMemo(() => {
    if (peerRubric === null || !group || group.members.length === 0) {
      return { peerOutcomeTable: [], criteriaMap: [] };
    }
    return transformPeerOutcomesTable(
      groupPeerOutcomes,
      peerRubric.criteria,
      group.members,
      currentMarkerId,
      selfEvaluationEnabled
    );
  }, [
    groupPeerOutcomes,
    peerRubric,
    group,
    currentMarkerId,
    selfEvaluationEnabled,
  ]);

  const handleDownload = () => {
    const csvContent = convertToCSV(peerOutcomeTable);
    const blob = new Blob([csvContent]);

    const link = document.createElement("a");

    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `peer_evaluation_group_${group?.id}.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const { pendingMarkIds, totalPendingEvaluations, actualTotalEvaluations } =
    useMemo(() => {
      return getPendingPeerEvaluations(
        data,
        !!data.assessment.settings?.selfEvaluationEnabled
      );
    }, [data]);

  const arePeerEvaluationsPending = totalPendingEvaluations > 0;

  // To to the main PeerEvaluation page, focused on an optional student
  const handleMarkStudent = (studentId?: string) => {
    const studentIdSearch = studentId ? `&student=${studentId}` : "";
    rootNavigate(`/peer-evaluation?tab=evaluation${studentIdSearch}`);
  };

  // If the Group is not formed yet, we do not show the PeerOverviewPage
  const isGroupWorkDefined = !!data?.studentWorkGroup?.id;
  if (!isGroupWorkDefined) {
    return <HiddenAssessment />;
  }

  // If the Charter is not accepted yet, we do not show the PeerOverviewPage
  if (
    data.assessment.settings?.groupCharterEnabled &&
    !data.studentWorkGroup?.charter?.latestAgreement
  ) {
    return <CharterNotAccepted />;
  }

  if (!data?.peerRubric?.id || actualTotalEvaluations < 1) {
    return (
      <>
        <div className={styles.peerContent}>
          <img src={checkboxImg} height={320} width={320} alt="" />
          <div className={styles.peerTextWrapper}>
            <Text kind="headingFour">Get Started with Peer Evaluation</Text>
            <Text kind="bodyLine">
              Your peer evaluation is available. Start now to give and receive
              valuable feedback on your group’s collaboration and your own
              contributions.
            </Text>
            <div>
              <Button
                style={{ marginTop: 12 }}
                kind="secondary"
                disabled={!isGroupWorkDefined}
                onClick={() => handleMarkStudent()}
              >
                Start peer evaluation
              </Button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div style={{ width: "100%" }}>
        {arePeerEvaluationsPending || !isPeerCompleted ? (
          <div className={styles.peerHeader}>
            <Text kind="headingFour">
              {totalPendingEvaluations}{" "}
              {`Evaluation${totalPendingEvaluations !== 1 ? "s" : ""} pending`}
            </Text>
            <div className={styles.filtersContainer}>
              {arePeerEvaluationsPending && (
                <Switch
                  label="Pending only"
                  labelPosition="right"
                  checked={filterPending}
                  onCheckedChange={(checked) => setFilterPending(checked)}
                />
              )}
              <Button
                icon={<PencilIcon label="" size={24} />}
                onClick={() => handleMarkStudent()}
              >
                Edit ratings and feedback
              </Button>
              <Button
                icon={<TickIcon label="" size={24} />}
                disabled={arePeerEvaluationsPending}
                onClick={onComplete}
              >
                Finalise and submit
              </Button>
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div className={styles.completedContainer}>
              <CheckRoundIcon label="" color={colors.success} />
              <Text kind="headingFive">
                Your evaluations have been submitted successfully.
              </Text>
            </div>
            <Button
              onClick={handleDownload}
              icon={<Icon iconName="Download" />}
            >
              Download
            </Button>
          </div>
        )}
      </div>
      <div className={styles.peerTableContainer}>
        <PeerOverviewTable
          me={data.me}
          filterByPending={filterPending}
          headers={criteriaMap}
          pendingMarkIds={pendingMarkIds}
          rows={peerOutcomeTable}
          onMarkStudent={handleMarkStudent}
        />
      </div>
    </>
  );
}

function HiddenAssessment() {
  return (
    <div className={styles.root} style={{ alignItems: "center" }}>
      <div className={styles.emptyContent}>
        <img src={assessmentImg} height={280} width={280} alt="" />
        <Text kind="headingThree" textAlign="center">
          Peer Evaluation will be visible once group formation is complete
        </Text>
        <Text kind="bodyLine" textAlign="center">
          Provide feedback on your team members&apos; contributions and receive
          input to improve collaboration and project results.
        </Text>
      </div>
    </div>
  );
}

function CharterNotAccepted() {
  return (
    <div className={styles.root} style={{ alignItems: "center" }}>
      <div className={styles.emptyContent}>
        <img src={assessmentImg} height={280} width={280} alt="" />
        <Text kind="headingThree" textAlign="center">
          Peer Evaluation will be visible once you accept the group charter
        </Text>
        <Text kind="bodyLine" textAlign="center">
          Provide feedback on your team members&apos; contributions and receive
          input to improve collaboration and project results.
        </Text>
      </div>
    </div>
  );
}

function convertToCSV(data: PeerOutcomeTable) {
  const criteriaOrder: { id: string; label: string }[] = [];
  data.forEach((user) => {
    user.outcomes.forEach((o) => {
      if (!criteriaOrder.some((c) => c.id === o.criteriaId)) {
        criteriaOrder.push({ id: o.criteriaId, label: o.criteriaValue });
      }
    });
  });

  // Build header rows
  const header1 = [
    "Group members",
    ...criteriaOrder.map((_, i) => `Criteria ${i + 1}`),
    "Status",
  ];
  const header2 = ["", ...criteriaOrder.map((c) => c.label), ""];

  const rows: string[][] = [];
  rows.push(header1);
  rows.push(header2);

  data.forEach((user) => {
    const row: string[] = [user.userName];
    criteriaOrder.forEach((criteria) => {
      const outcome = user.outcomes.find((o) => o.criteriaId === criteria.id);
      row.push(outcome ? outcome.scaleValue : "");
    });
    row.push("Done");
    rows.push(row);
  });

  // Convert to CSV
  return rows.map((r) => r.map((v) => `"${v}"`).join(",")).join("\n");
}
